/**
 * Founder Control Center v2 · Operations · Reservas overview.
 *
 * Vista agregada platform-wide de reservas (appointments) basada únicamente en
 * los KPIs que el backend YA expone vía `/api/founder-os/home` (funnel_30d) y
 * `/api/founder-os/reports/weekly` (activity.appointments).
 *
 * 0 backend tocado · 0 endpoints nuevos · 0 mocks. No hay endpoint platform-wide
 * para listar appointments cross-org hoy: cuando exista (Sprint posterior) este
 * panel podrá enriquecerse con tabla detalle. Mientras tanto: agregados reales
 * + links honestos al flujo per-org (`/calendar` · `/availability`).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { CalendarBlank, ArrowsClockwise, ArrowRight, ChartLineUp, Clock } from "@phosphor-icons/react";

export default function ReservasOverview() {
  const [home, setHome] = useState(null);
  const [weekly, setWeekly] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = async () => {
    setLoading(true); setErr(null);
    const [h, w] = await Promise.allSettled([
      api.get("/founder-os/home"),
      api.get("/founder-os/reports/weekly"),
    ]);
    if (h.status === "fulfilled") setHome(h.value.data);
    else setErr(h.reason?.response?.data?.detail || "No se pudieron cargar agregados");
    if (w.status === "fulfilled") setWeekly(w.value.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const fn = home?.funnel_30d;
  const wk = weekly?.activity;
  const handoffs = home?.saas?.handoffs_30d; // si existe

  return (
    <div className="space-y-4" data-testid="reservas-overview-root">
      <header className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--ag-gold)] font-semibold flex items-center gap-1.5">
            <CalendarBlank size={11} weight="fill" /> Operations · Reservas
          </div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-[var(--ag-text)] mt-1">
            Reservas de la plataforma
          </h2>
          <p className="text-xs text-[var(--ag-text-dim)] mt-1">
            Agregados cross-org en vivo. El drill-down per-centro se realiza desde el calendario de cada org.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-1.5 text-xs text-[var(--ag-text-dim)] hover:text-[var(--ag-gold)] px-3 py-2 rounded border border-[var(--ag-border)] hover:border-[var(--ag-gold)]/50 disabled:opacity-50"
          data-testid="reservas-refresh"
        >
          <ArrowsClockwise size={13} className={loading ? "animate-spin" : ""} /> Refrescar
        </button>
      </header>

      {err && (
        <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded px-3 py-2" data-testid="reservas-error">
          {err}
        </div>
      )}

      {/* KPIs principales */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="reservas-kpis">
        <KpiBox label="Reservas · 30d" value={fn?.appointments ?? "—"} sublabel="Citas creadas" testid="reservas-kpi-30d" />
        <KpiBox label="Reservas · 7d"  value={wk?.appointments ?? "—"} sublabel="Última semana" testid="reservas-kpi-7d" />
        <KpiBox label="Trial→Sub %"    value={fn?.trial_to_sub_pct != null ? `${fn.trial_to_sub_pct}%` : "—"} sublabel="Conversión funnel" testid="reservas-kpi-trial-sub" />
        <KpiBox label="Handoffs IA→reserva" value={handoffs ?? "—"} sublabel="Chat → cita" testid="reservas-kpi-handoffs" />
      </section>

      {/* Top orgs por leads (proxy de actividad reservas) */}
      {weekly?.top_orgs_by_leads?.length > 0 && (
        <section className="rounded-lg border border-[var(--ag-border)] bg-[var(--ag-surface)] p-4" data-testid="reservas-top-orgs">
          <div className="flex items-center gap-2 mb-3">
            <ChartLineUp size={14} weight="duotone" className="text-[var(--ag-gold)]" />
            <h3 className="text-xs uppercase tracking-[0.22em] text-[var(--ag-text-dim)] font-semibold">
              Top 5 centros por actividad esta semana
            </h3>
          </div>
          <ol className="space-y-1.5 text-xs">
            {weekly.top_orgs_by_leads.map((o, i) => (
              <li key={`${o.org}-${i}`} className="flex items-center justify-between border-b border-[var(--ag-border)] py-1.5">
                <span className="text-[var(--ag-text-dim)]">{i + 1}. {o.org}</span>
                <span className="font-bold tabular-nums text-[var(--ag-gold)]">{o.leads} leads</span>
              </li>
            ))}
          </ol>
          <p className="text-[10px] text-[var(--ag-text-muted)] mt-3">
            Indicador proxy: leads → predictor de reservas. El listado cross-org de appointments se habilitará en Sprint posterior.
          </p>
        </section>
      )}

      {/* Drill-down per-org */}
      <section className="rounded-lg border border-[var(--ag-gold)]/25 bg-[var(--ag-gold)]/5 p-4" data-testid="reservas-drilldown">
        <div className="flex items-center gap-2 mb-3">
          <Clock size={14} weight="duotone" className="text-[var(--ag-gold)]" />
          <h3 className="text-xs uppercase tracking-[0.22em] text-[var(--ag-gold)] font-semibold">
            Drill-down: ver reservas concretas
          </h3>
        </div>
        <p className="text-xs text-[var(--ag-text-dim)]">
          El listado completo y la gestión per-cita viven en el contexto operador de cada org. Accede vía:
        </p>
        <div className="grid sm:grid-cols-2 gap-2 mt-3">
          <DrillLink to="/calendar" label="Mi calendario (org actual)" sublabel="Vista operador · /api/appointments" testid="reservas-link-calendar" />
          <DrillLink to="/availability" label="Disponibilidad Gantt" sublabel="Por operador · /appointments/gantt" testid="reservas-link-availability" />
        </div>
        <div className="mt-3 pt-3 border-t border-[var(--ag-gold)]/20 text-[10px] text-[var(--ag-text-muted)]">
          Cross-org listing futuro: pendiente sprint backend (sin endpoint hoy).
        </div>
      </section>
    </div>
  );
}

function KpiBox({ label, value, sublabel, testid }) {
  return (
    <div className="rounded-lg border border-[var(--ag-border)] bg-[var(--ag-surface)] p-3.5" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-wider text-[var(--ag-text-muted)] font-semibold">{label}</div>
      <div className="font-display text-2xl font-black tabular-nums mt-1 leading-none">{value}</div>
      <div className="text-[10px] text-[var(--ag-text-dim)] mt-1">{sublabel}</div>
    </div>
  );
}

function DrillLink({ to, label, sublabel, testid }) {
  return (
    <Link
      to={to}
      className="group rounded border border-[var(--ag-border)] bg-[var(--ag-surface)] p-3 hover:border-[var(--ag-gold)]/50 hover:bg-[var(--ag-gold)]/5 transition-colors flex items-center justify-between"
      data-testid={testid}
    >
      <div>
        <div className="font-display text-sm font-bold tracking-tight">{label}</div>
        <div className="text-[10px] text-[var(--ag-text-dim)]">{sublabel}</div>
      </div>
      <ArrowRight size={12} className="text-[var(--ag-text-muted)] group-hover:text-[var(--ag-gold)]" />
    </Link>
  );
}
